/**
 * 「업로드 N일차」 지표.
 *
 * 게시물 표의 「1일차·3일차·7일차…」 열과 캠페인 상세 「열 설정」의 지표별 「{지표} 일차별」 줄이 이것을 쓴다.
 */
import type { MomentMetricEntry } from 'src/types/moments/moment-domain';
import type { SocialPostInput } from 'src/utils/moments/domain/social-post-input';

import { engagementRatePctOf } from 'src/utils/metrics/engagement-rate';
import { isMetricCountField } from 'src/utils/metrics/metric-field-keys';

/** 지표 상세: 게시 후 N일차(D+N) 칼럼 정의 */
export const VIEW_DAY_COLUMNS = [
  { day: 1, field: 'viewDay1', label: '1일' },
  { day: 3, field: 'viewDay3', label: '3일' },
  { day: 7, field: 'viewDay7', label: '7일' },
  { day: 30, field: 'viewDay30', label: '30일' },
];

/** 1·3·7·30일 열에서 고를 수 있는 지표 */
export const METRIC_DAY_DETAIL_OPTIONS = [
  { value: 'viewCount', label: '조회수', excelPrefix: '조회수' },
  { value: 'likeCount', label: '좋아요', excelPrefix: '좋아요' },
  { value: 'commentCount', label: '댓글', excelPrefix: '댓글' },
  { value: 'savedCount', label: '저장', excelPrefix: '저장' },
  { value: 'shareCount', label: '공유', excelPrefix: '공유' },
  { value: 'downloadCount', label: '다운로드', excelPrefix: '다운로드' },
];

/** 지표 값(`savedCount`) → 포스트 표의 반응 열 이름(`saved`) — 숨긴 지표 집합은 열 이름으로 온다 */
const METRIC_FIELD_BY_DAY_OPTION: Readonly<Record<string, string>> = Object.freeze({
  likeCount: 'like',
  commentCount: 'comment',
  savedCount: 'saved',
  shareCount: 'share',
  downloadCount: 'download',
});

/**
 * 1·3·7·30일 열을 세울 수 있는 지표 — 숨긴 반응 지표는 빠진다(조회수는 늘 남는다).
 * @param hiddenMetricFields 숨긴 반응 열 이름(`like` · `saved` …)
 */
export function dayMetricOptionsFor(hiddenMetricFields?: ReadonlySet<string>): typeof METRIC_DAY_DETAIL_OPTIONS {
  return METRIC_DAY_DETAIL_OPTIONS.filter((opt) => !hiddenMetricFields?.has(METRIC_FIELD_BY_DAY_OPTION[opt.value] ?? ''));
}

function msToYyyymmddUtc(ms: number): string {
  const d = new Date(ms);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}${m}${day}`;
}

function calendarDaysAfterYyyymmdd(uploadYmd: string, metricYmd: string): number | null {
  const u = String(uploadYmd);
  const m = String(metricYmd);
  if (!/^\d{8}$/.test(u) || !/^\d{8}$/.test(m)) return null;
  const t0 = Date.UTC(Number(u.slice(0, 4)), Number(u.slice(4, 6)) - 1, Number(u.slice(6, 8)));
  const t1 = Date.UTC(Number(m.slice(0, 4)), Number(m.slice(4, 6)) - 1, Number(m.slice(6, 8)));
  return Math.round((t1 - t0) / 86400000);
}

function resolvePostUploadMs(post: SocialPostInput | null | undefined): number | null {
  const raw = post?.timestamp ?? post?.postDate ?? post?.date;
  if (raw == null) return null;
  if (typeof raw === 'number') return raw < 1e12 ? raw * 1000 : raw;
  const asNumber = Number(raw);
  if (Number.isFinite(asNumber) && String(raw).replace(/\D/g, '').length >= 10) {
    return asNumber < 1e12 ? asNumber * 1000 : asNumber;
  }
  if (/^\d{8}$/.test(String(raw).trim())) {
    const s = String(raw).trim();
    return Date.UTC(Number(s.slice(0, 4)), Number(s.slice(4, 6)) - 1, Number(s.slice(6, 8)));
  }
  const parsed = new Date(String(raw).replace(' ', 'T'));
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
}

function readMetricFieldValue(
  metric: MomentMetricEntry | null | undefined,
  metricField: string,
): number | null {
  if (!metric || typeof metric !== 'object') return null;
  if (metricField === 'engagementRate') {
    const rate = engagementRatePctOf(metric);
    return rate == null ? null : Number(rate.toFixed(2));
  }
  // 원본은 `metric[metricField]`로 아무 키나 읽었다. 실제 호출부(METRIC_DAY_DETAIL_OPTIONS)는
  // 카운트 6종 + 'engagementRate'만 넘기고, 그 외 키는 원본에서도 NaN → null이었다.
  const n = Number(isMetricCountField(metricField) ? metric[metricField] : undefined);
  return Number.isFinite(n) ? n : null;
}

/**
 * 게시 후 targetDay일차(D+N) 시점 지표 값.
 * 정확히 D+N이 있으면 사용. 없으면 직전일(D+N-1)만 대체. 2일 이상 이전이면 null.
 * @param {string} [metricField='viewCount'] - metrics 엔트리 필드명
 * @returns {number|null}
 */
export function getMetricAtDay(
  post: SocialPostInput | null | undefined,
  targetDay: number,
  metricField: string = 'viewCount',
): number | null {
  if (!post?.metrics || typeof post.metrics !== 'object') return null;
  if (post?.isRepost === true) return 0;

  const uploadMs = resolvePostUploadMs(post);
  if (uploadMs == null) return null;
  const uploadYyyymmdd = msToYyyymmddUtc(uploadMs);
  const field = String(metricField || 'viewCount');

  // 원본은 `let best`를 콜백 안에서 갱신했는데, TS는 콜백 내부 대입을 추적하지 못해
  // 콜백 이후 `best`를 계속 null로 본다. 후보를 모아 마지막에 고르는 형태로 바꿨다 —
  // 동점이면 먼저 만난 항목을 남기는 것까지 원본과 동일하다.
  const candidates: { calDiff: number; value: number }[] = [];
  Object.entries(post.metrics).forEach(([dateKey, metric]) => {
    if (!metric || typeof metric !== 'object') return;
    const keyYmd = /^\d{8}$/.test(String(dateKey))
      ? String(dateKey)
      : String(metric?.requestDateTime ?? metric?.metricDate ?? '').replace(/\D/g, '').slice(0, 8);
    if (!/^\d{8}$/.test(keyYmd)) return;

    const calDiff = calendarDaysAfterYyyymmdd(uploadYyyymmdd, keyYmd);
    if (calDiff == null || calDiff > targetDay || calDiff < targetDay - 1) return;

    const value = readMetricFieldValue(metric, field);
    if (value == null) return;

    candidates.push({ calDiff, value });
  });

  if (candidates.length === 0) return null;
  const best = candidates.reduce((acc, cur) => (cur.calDiff > acc.calDiff ? cur : acc));
  return best.value;
}
